import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Trash2, Users } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";

export default function MembershipsPage() {
  const queryClient = useQueryClient();

  const membershipsQ = useQuery({
    queryKey: ["carpintel_memberships"],
    queryFn: () => fetch("/app-api/memberships").then((r) => r.json()),
  });

  const providersQ = useQuery({
    queryKey: ["carpintel_providers"],
    queryFn: () => fetch("/app-api/directory/providers").then((r) => r.json()),
  });

  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);

  const memberships: any[] = membershipsQ.data ?? [];
  const providers: any[] = providersQ.data ?? [];

  const memberProviderIds = new Set(
    memberships.map((m: any) => m.access_provider_id),
  );

  const filtered = providers.filter((p: any) => {
    if (!search.trim()) return true;
    return p.name.toLowerCase().includes(search.trim().toLowerCase());
  });

  async function addMembership(providerId: string) {
    setAdding(providerId);
    try {
      const res = await fetch("/app-api/memberships", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_provider_id: providerId }),
      });
      if (!res.ok) throw new Error();
      queryClient.invalidateQueries({ queryKey: ["carpintel_memberships"] });
    } catch {
      /* keep UI usable */
    }
    setAdding(null);
  }

  async function removeMembership(membershipId: number) {
    setRemoving(membershipId);
    try {
      const res = await fetch(`/app-api/memberships/${membershipId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
      queryClient.invalidateQueries({ queryKey: ["carpintel_memberships"] });
    } catch {
      /* keep UI usable */
    }
    setRemoving(null);
  }

  const loading = membershipsQ.isLoading || providersQ.isLoading;

  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/lakes"
          className="text-sm font-medium text-accent hover:underline"
        >
          My lakes
        </Link>
        <div className="mt-2 flex min-w-0 items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset text-secondary">
            <Users />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-primary">
              My memberships
            </h1>
            <p className="mt-1 text-sm text-secondary">
              Save your club and fishery memberships. CarpIntel highlights
              waters you can already access.
            </p>
          </div>
        </div>
      </header>

      {/* Search / add */}
      <div className="space-y-3 rounded-lg border border-border bg-raised p-4">
        <label
          className="block text-sm font-medium text-primary"
          htmlFor="membership-search"
        >
          Search providers
        </label>
        <Input
          id="membership-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search memberships…"
        />

        {loading ? (
          <p className="text-sm text-secondary">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-secondary">No providers match.</p>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((p: any) => {
              const isMember = memberProviderIds.has(p.id);
              const membership = memberships.find(
                (m: any) => m.access_provider_id === p.id,
              );
              return (
                <li
                  key={p.id}
                  className="flex min-w-0 items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-primary">
                      {p.name}
                    </p>
                    <p className="text-xs text-secondary capitalize">
                      {p.type.replace(/_/g, " ")}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {isMember ? (
                      <>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-success">
                          <CheckCircle className="size-4" /> Member
                        </span>
                        <Button
                          variant="tertiary"
                          size="sm"
                          isLoading={removing === membership?.id}
                          onClick={() =>
                            removeMembership(Number(membership?.id))
                          }
                          aria-label={`Remove ${p.name}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        isLoading={adding === p.id}
                        onClick={() => addMembership(p.id)}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Current memberships summary */}
      {memberships.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary">
            Your memberships ({memberships.length})
          </h2>
          <div className="space-y-2">
            {memberships.map((m: any) => (
              <div
                key={m.id}
                className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border bg-raised px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-primary">
                    {m.provider_name}
                  </p>
                  <p className="text-xs capitalize text-secondary">
                    {m.provider_type?.replace(/_/g, " ")}
                  </p>
                </div>
                <Button
                  variant="tertiary"
                  size="sm"
                  isLoading={removing === m.id}
                  onClick={() => removeMembership(Number(m.id))}
                  aria-label={`Remove ${m.provider_name}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
